import Phaser from 'phaser';
import type { MapData } from '@/types/map-data';
import type { GhostName } from '@/types/ghost';
import type { RenderedMap } from '@/maps/map-renderer';
import { getMapByIndex, getTotalMapCount } from '@/maps/map-loader';
import { renderMap, tileToPixelX, tileToPixelY } from '@/maps/map-renderer';
import { PacmanEntity } from '@/entities/pacman';
import { GhostEntity } from '@/entities/ghost-entity';
import { FruitEntity } from '@/entities/fruit-entity';
import { HudDisplay } from '@/ui/hud';
import { KeyboardInput } from '@/input/keyboard-input';
import { MobileInput } from '@/input/mobile-input';
import { createInitialGameState } from '@/config/game-state';
import type { GameState } from '@/config/game-state';
import { SCORING } from '@/config/scoring';
import { getTopScores } from '@/storage/score-repository';
import { playDeathSound, playGameOverSound, playPowerPillSound, playExtraLifeSound, playLevelCompleteSound, playFruitPickupSound, playGhostEatSound, playWakaWakaSound } from '@/audio/sound-generator';
import { ConfettiEffect } from '@/effects/confetti';
import { ScorePopupManager } from '@/effects/score-popup';
import { GhostEatBurstManager } from '@/effects/ghost-eat-burst';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/game-config';
import { TIMING, GHOST_MODE_CYCLES, SPEED } from '@/config/speed';
import { MazeGraph } from '@/ai/pathfinding';

const GHOST_NAMES: ReadonlyArray<GhostName> = ['blinky', 'speedy', 'clyde', 'inky'] as const;

/** Scatter corner targets (same as ghost-entity, extracted for graph construction). */
function getScatterTargetForGraph(name: GhostName, mapWidth: number, mapHeight: number): { x: number; y: number } {
  switch (name) {
    case 'blinky': return { x: mapWidth - 3, y: 0 };
    case 'speedy': return { x: 2, y: 0 };
    case 'clyde': return { x: 0, y: mapHeight - 1 };
    case 'inky': return { x: mapWidth - 1, y: mapHeight - 1 };
  }
}

const DEATH_ANIMATION_MS = 1200;
const DEATH_PAUSE_AFTER_MS = 500;
const COLLISION_DISTANCE_PX = 10;
const CELEBRATION_DURATION_MS = 2500;
const PROMPT_FADE_IN_MS = 500;

export class GameScene extends Phaser.Scene {
  private mapData: MapData | null = null;
  private renderedMap: RenderedMap | null = null;
  private pacman: PacmanEntity | null = null;
  private ghosts: GhostEntity[] = [];
  private fruit: FruitEntity | null = null;
  private hud: HudDisplay | null = null;
  private keyboardInput: KeyboardInput | null = null;
  private mobileInput: MobileInput | null = null;
  private gameState: GameState = createInitialGameState();
  private readyText: Phaser.GameObjects.Text | null = null;
  private readyTimer = 0;
  private isReady = false;
  private isDying = false;
  private deathTimer = 0;
  private deathPhase: 'animation' | 'pause' | 'none' = 'none';
  private isGameOver = false;
  private isLevelComplete = false;
  private levelCompleteTimer = 0;
  private levelCompleteText: Phaser.GameObjects.Text | null = null;
  private confettiEffect: ConfettiEffect | null = null;
  private pressAnyKeyText: Phaser.GameObjects.Text | null = null;
  private levelCompletePhase: 'celebration' | 'waiting' | 'none' = 'none';
  private isPaused = false;
  private pauseKeyHandler: ((event: KeyboardEvent) => void) | null = null;
  private resumeHandler: (() => void) | null = null;
  private vulnerabilityTimer = 0;
  private isVulnerabilityActive = false;
  private vulnerabilityDuration: number = TIMING.POWER_PILL_BASE_DURATION_MS;
  private scorePopups: ScorePopupManager | null = null;
  private ghostEatBursts: GhostEatBurstManager | null = null;
  private ghostModeCycleIndex = 0;
  private ghostModeTimer = 0;
  private currentGhostBehavior: 'scatter' | 'chase' = 'scatter';
  private globalSpeedMultiplier = 1;
  private mazeGraph: MazeGraph | null = null;
  private fpsText: Phaser.GameObjects.Text | null = null;
  private fpsUpdateTimer = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(data?: { score?: number; lives?: number; level?: number; extraLifeAwarded?: boolean; collectedFruits?: string[] }): void {
    this.cleanup();

    if (data && data.level !== undefined && data.level > 1) {
      // Continuing from previous level - preserve score, lives, etc.
      this.gameState = createInitialGameState();
      this.gameState.score = data.score ?? 0;
      this.gameState.lives = data.lives ?? SCORING.STARTING_LIVES;
      this.gameState.level = data.level;
      this.gameState.extraLifeAwarded = data.extraLifeAwarded ?? false;
      this.gameState.collectedFruits = data.collectedFruits ?? [];
    } else {
      this.gameState = createInitialGameState();
    }

    this.isReady = false;
    this.readyTimer = 0;
    this.isDying = false;
    this.deathTimer = 0;
    this.deathPhase = 'none';
    this.isGameOver = false;
    this.isLevelComplete = false;
    this.levelCompleteTimer = 0;
    this.levelCompletePhase = 'none';
    this.isPaused = false;
    this.vulnerabilityTimer = 0;
    this.isVulnerabilityActive = false;
    this.ghostModeCycleIndex = 0;
    this.ghostModeTimer = 0;
    this.currentGhostBehavior = 'scatter';
    // Vulnerability duration decreases with level, minimum 2s
    this.vulnerabilityDuration = Math.max(
      TIMING.POWER_PILL_MIN_DURATION_MS,
      TIMING.POWER_PILL_BASE_DURATION_MS - (this.gameState.level - 1) * 500,
    );

    // Load map (cycle through maps, loop after all maps completed)
    const totalMaps = getTotalMapCount();
    const mapIndex = (this.gameState.level - 1) % totalMaps;
    this.mapData = getMapByIndex(mapIndex);

    // Calculate global speed multiplier: +10% per completed loop of all maps
    const loopsCompleted = Math.floor((this.gameState.level - 1) / totalMaps);
    this.globalSpeedMultiplier = 1 + loopsCompleted * SPEED.LOOP_SPEED_INCREMENT;

    // Render map
    this.renderedMap = renderMap(this, this.mapData);
    this.gameState.totalDots = this.renderedMap.totalDots;

    // Create Pac-Man (with global speed multiplier for loop escalation)
    this.pacman = new PacmanEntity(this, this.mapData, this.globalSpeedMultiplier);

    // Start warp-in animation for Pac-Man
    this.playWarpInAnimation();

    // Create ghosts in base
    this.createGhosts();

    // Create fruit spawner
    this.fruit = new FruitEntity(this, this.mapData);

    // Create effects managers
    this.scorePopups = new ScorePopupManager(this);
    this.ghostEatBursts = new GhostEatBurstManager(this);

    // Create HUD
    this.hud = new HudDisplay(this);
    this.updateHud();

    // Setup keyboard
    this.keyboardInput = new KeyboardInput(this);

    // Setup mobile controls (tilt or touch fallback) if on mobile device
    if (MobileInput.isMobileDevice()) {
      this.mobileInput = new MobileInput(this);
    }

    // Load high score
    this.loadHighScore();

    // Setup pause keys
    this.setupPauseKeys();

    // Listen for scene resume (when PauseScene stops and resumes us)
    this.resumeHandler = () => {
      this.isPaused = false;
    };
    this.events.on('resume', this.resumeHandler);

    // FPS counter (for performance verification, shown via ?fps=1 query param)
    if (new URLSearchParams(window.location.search).get('fps') === '1') {
      this.fpsText = this.add.text(4, GAME_HEIGHT - 16, 'FPS: --', {
        fontFamily: '"Courier New", monospace',
        fontSize: '10px',
        color: '#00FF00',
      }).setDepth(250).setScrollFactor(0);
    }

    // Show "READY!" text
    this.showReadyText();

  }

  update(time: number, delta: number): void {
    // Update FPS counter (always runs for accurate measurement)
    if (this.fpsText) {
      this.fpsUpdateTimer += delta;
      if (this.fpsUpdateTimer >= 500) {
        this.fpsUpdateTimer = 0;
        const fps = Math.round(this.game.loop.actualFps);
        this.fpsText.setText(`FPS: ${fps}`);
      }
    }

    // Update mobile input detection (gyro vs touch fallback) — run even during
    // READY/death phases so the gyroscope detection timeout can fire early.
    if (this.mobileInput) {
      this.mobileInput.update(delta);
    }

    // Handle game over state - freeze everything
    if (this.isGameOver) {
      return;
    }

    // Handle level complete state
    if (this.isLevelComplete) {
      this.levelCompleteTimer += delta;

      // Update confetti effect
      if (this.confettiEffect) {
        this.confettiEffect.update(delta);
      }

      if (this.levelCompletePhase === 'celebration') {
        // After celebration duration, show "press any key" prompt
        if (this.levelCompleteTimer >= CELEBRATION_DURATION_MS) {
          this.levelCompletePhase = 'waiting';
          this.showPressAnyKeyPrompt();
        }
      }
      // In 'waiting' phase, input is handled by keyboard listener
      return;
    }

    // Handle death sequence
    if (this.isDying) {
      this.deathTimer += delta;

      if (this.deathPhase === 'animation') {
        // Update the death animation on Pac-Man
        if (this.pacman) {
          this.pacman.updateDeathAnimation(delta);
        }
        if (this.deathTimer >= DEATH_ANIMATION_MS) {
          this.deathPhase = 'pause';
          this.deathTimer = 0;
          if (this.pacman) {
            this.pacman.setVisible(false);
            this.pacman.stopDeathAnimation();
          }
        }
      } else if (this.deathPhase === 'pause') {
        if (this.deathTimer >= DEATH_PAUSE_AFTER_MS) {
          this.isDying = false;
          this.deathTimer = 0;
          this.deathPhase = 'none';
          this.respawnAfterDeath();
        }
      }
      return;
    }

    if (!this.isReady) {
      this.readyTimer += delta;
      if (this.readyTimer >= 2000) {
        this.isReady = true;
        this.hideReadyText();
        this.releaseAllGhosts();
      }
      return;
    }

    if (!this.pacman || !this.keyboardInput || !this.mapData || !this.renderedMap) {
      return;
    }

    // Handle input - prefer mobile direction if available, fallback to keyboard
    const keyDirection = this.keyboardInput.getDirection();
    const mobileDirection = this.mobileInput ? this.mobileInput.getDirection() : 'none';
    const direction = mobileDirection !== 'none' ? mobileDirection : keyDirection;
    this.pacman.setDirection(direction);

    // Update Pac-Man
    this.pacman.update(time, delta);

    // Update ghost chase/scatter mode cycle
    this.updateGhostModeCycle(delta);

    // Update ghosts (pass Pac-Man position + direction for AI targeting)
    const pacTileX = this.pacman.getTileX();
    const pacTileY = this.pacman.getTileY();
    const pacDirection = this.pacman.getCurrentDirection();
    for (const ghost of this.ghosts) {
      ghost.setPacManPosition(pacTileX, pacTileY);
      ghost.setPacManDirection(pacDirection);

      // Provide other ghost positions so Clyde can avoid stacking
      const otherPositions = this.ghosts
        .filter(g => g !== ghost && g.isReleased() && !g.isReturning())
        .map(g => ({ x: g.getTileX(), y: g.getTileY() }));
      ghost.setOtherGhostPositions(otherPositions);

      ghost.update(time, delta);
    }

    // Check dot collection
    this.checkDotCollection();

    // Check power pill collection
    this.checkPowerPillCollection();

    // Update vulnerability timer
    this.updateVulnerabilityTimer(delta);

    // Update fruit spawning and collection
    this.updateFruit(delta);

    // Check level complete (all dots and power pills eaten)
    this.checkLevelComplete();

    // Check ghost collision (skip if level just completed)
    if (!this.isLevelComplete) {
      this.checkGhostCollision();
    }

    // Update effects
    if (this.scorePopups) {
      this.scorePopups.update(delta);
    }
    if (this.ghostEatBursts) {
      this.ghostEatBursts.update(delta);
    }

    // Update HUD
    if (this.hud) {
      this.hud.update(delta);
    }
  }

  private createGhosts(): void {
    if (!this.mapData) {
      return;
    }

    const releaseX = this.mapData.ghostReleasePoint.x;
    const releaseY = this.mapData.ghostReleasePoint.y;

    // Build scatter target map for all ghosts
    const scatterTargets = new Map<GhostName, { x: number; y: number }>();
    for (const name of GHOST_NAMES) {
      const target = getScatterTargetForGraph(name, this.mapData.width, this.mapData.height);
      scatterTargets.set(name, target);
    }

    // Build cached pathfinding graph for this level's maze
    const exitTargetX = releaseX;
    const exitTargetY = releaseY - 1;
    this.mazeGraph = new MazeGraph(
      this.mapData,
      exitTargetX,
      exitTargetY,
      scatterTargets,
    );

    for (const name of GHOST_NAMES) {
      const ghost = new GhostEntity(this, name, releaseX, releaseY, this.mapData, this.mazeGraph, this.globalSpeedMultiplier);
      this.ghosts.push(ghost);
    }
  }

  private releaseAllGhosts(): void {
    for (const ghost of this.ghosts) {
      ghost.release();
    }
  }

  private showReadyText(): void {
    if (!this.mapData) {
      return;
    }
    const centerX = tileToPixelX(this.mapData.width / 2);
    const centerY = tileToPixelY(this.mapData.height / 2 + 3);

    this.readyText = this.add.text(centerX, centerY, 'READY!', {
      fontFamily: '"Orbitron", "Rajdhani", "Segoe UI", sans-serif',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#FFE500',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 12, fill: true },
    }).setOrigin(0.5);
  }

  private hideReadyText(): void {
    if (this.readyText) {
      this.readyText.destroy();
      this.readyText = null;
    }
  }

  private checkDotCollection(): void {
    if (!this.pacman || !this.renderedMap) {
      return;
    }

    const pacTileX = this.pacman.getTileX();
    const pacTileY = this.pacman.getTileY();

    for (const dot of this.renderedMap.dots) {
      if (!dot.eaten && dot.tileX === pacTileX && dot.tileY === pacTileY) {
        dot.eaten = true;
        dot.graphics.setVisible(false);
        dot.glow.setVisible(false);
        this.gameState.score += SCORING.DOT;
        this.gameState.dotsEaten++;
        this.updateHud();
        this.checkExtraLife();
        playWakaWakaSound(this);
      }
    }
  }

  private checkPowerPillCollection(): void {
    if (!this.pacman || !this.renderedMap) {
      return;
    }

    const pacTileX = this.pacman.getTileX();
    const pacTileY = this.pacman.getTileY();

    for (const pill of this.renderedMap.powerPills) {
      if (!pill.eaten && pill.tileX === pacTileX && pill.tileY === pacTileY) {
        pill.eaten = true;
        pill.graphics.setVisible(false);
        pill.glow.setVisible(false);
        this.gameState.score += SCORING.POWER_PILL;
        this.gameState.dotsEaten++;
        this.updateHud();
        this.checkExtraLife();
        this.activateGhostVulnerability();
      }
    }
  }

  private activateGhostVulnerability(): void {
    // Play power pill activation sound
    playPowerPillSound(this);

    // Reset vulnerability timer (allows re-triggering with a new power pill)
    this.vulnerabilityTimer = 0;
    this.isVulnerabilityActive = true;

    // Set all released ghosts to frightened mode (skip returning ghosts)
    for (const ghost of this.ghosts) {
      if (ghost.isReleased() && !ghost.isReturning()) {
        ghost.setFrightened(true);
      }
    }

    // Reset ghost eat combo counter
    this.gameState.ghostCombo = 0;
  }

  private updateVulnerabilityTimer(delta: number): void {
    if (!this.isVulnerabilityActive) {
      return;
    }

    this.vulnerabilityTimer += delta;

    // Start flashing warning when nearing expiration
    const timeRemaining = this.vulnerabilityDuration - this.vulnerabilityTimer;
    if (timeRemaining <= TIMING.POWER_PILL_FLASH_WARNING_MS && timeRemaining > 0) {
      for (const ghost of this.ghosts) {
        if (ghost.isFrightened() && !ghost.isFlashing()) {
          ghost.setFlashing(true);
        }
      }
    }

    if (this.vulnerabilityTimer >= this.vulnerabilityDuration) {
      // Vulnerability expired - return ghosts to normal
      this.isVulnerabilityActive = false;
      this.vulnerabilityTimer = 0;

      for (const ghost of this.ghosts) {
        ghost.setFrightened(false);
        // Restore current chase/scatter behavior mode
        if (!ghost.isReturning()) {
          ghost.setBehaviorMode(this.currentGhostBehavior);
        }
      }
    }
  }

  private updateGhostModeCycle(delta: number): void {
    // Don't advance mode cycle during vulnerability (frightened mode takes priority)
    if (this.isVulnerabilityActive) {
      return;
    }

    this.ghostModeTimer += delta;

    // Check if we're past all defined cycles (permanent chase)
    if (this.ghostModeCycleIndex >= GHOST_MODE_CYCLES.length) {
      // Permanent chase mode
      if (this.currentGhostBehavior !== 'chase') {
        this.currentGhostBehavior = 'chase';
        this.applyGhostBehaviorMode('chase');
      }
      return;
    }

    const currentCycle = GHOST_MODE_CYCLES[this.ghostModeCycleIndex];
    if (!currentCycle) {
      return;
    }

    if (this.ghostModeTimer >= currentCycle.duration) {
      // Advance to next cycle
      this.ghostModeTimer = 0;
      this.ghostModeCycleIndex++;

      // Determine new mode
      const nextCycle = GHOST_MODE_CYCLES[this.ghostModeCycleIndex];
      const newMode = nextCycle ? nextCycle.mode : 'chase';

      if (newMode !== this.currentGhostBehavior) {
        this.currentGhostBehavior = newMode;
        this.applyGhostBehaviorMode(newMode);
      }
    }
  }

  private applyGhostBehaviorMode(mode: 'scatter' | 'chase'): void {
    for (const ghost of this.ghosts) {
      // Only change behavior mode for non-frightened, non-returning ghosts
      if (!ghost.isFrightened() && !ghost.isReturning()) {
        ghost.setBehaviorMode(mode);
      }
    }
  }

  private updateFruit(delta: number): void {
    if (!this.fruit || !this.pacman) {
      return;
    }

    // Check if fruit should spawn based on dots eaten
    this.fruit.checkSpawn(this.gameState.dotsEaten, this.gameState.level);

    // Update fruit timer (handles despawning)
    this.fruit.update(delta);

    // Check if Pac-Man collected the fruit
    const fruitScore = this.fruit.checkCollection(
      this.pacman.getTileX(),
      this.pacman.getTileY(),
    );

    if (fruitScore > 0) {
      // Track collected fruit name for HUD fruit counter
      const fruitName = this.fruit.getCurrentFruitName();
      if (fruitName) {
        this.gameState.collectedFruits.push(fruitName);
      }

      this.gameState.score += fruitScore;
      this.updateHud();
      this.checkExtraLife();
      playFruitPickupSound(this);

      // Show score popup at fruit position
      if (this.scorePopups && this.fruit) {
        this.scorePopups.spawn(this.fruit.getPixelX(), this.fruit.getPixelY(), fruitScore);
      }
    }
  }

  private checkLevelComplete(): void {
    if (this.isLevelComplete || this.gameState.dotsEaten < this.gameState.totalDots) {
      return;
    }

    // All dots and power pills eaten - level complete!
    this.isLevelComplete = true;
    this.levelCompleteTimer = 0;

    // Freeze all entities
    for (const ghost of this.ghosts) {
      ghost.setVisible(false);
    }

    // Play level complete fanfare
    playLevelCompleteSound(this);

    // Show level complete message
    this.showLevelCompleteMessage();
  }

  private showLevelCompleteMessage(): void {
    if (!this.mapData) {
      return;
    }

    this.levelCompletePhase = 'celebration';

    const centerX = tileToPixelX(this.mapData.width / 2);
    const centerY = tileToPixelY(this.mapData.height / 2 + 1);

    this.levelCompleteText = this.add.text(centerX, centerY, 'LEVEL COMPLETE!', {
      fontFamily: '"Orbitron", "Rajdhani", "Segoe UI", sans-serif',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#00FF88',
      shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 14, fill: true },
    }).setOrigin(0.5).setDepth(90);

    // Add flashing effect
    this.tweens.add({
      targets: this.levelCompleteText,
      alpha: { from: 1, to: 0.3 },
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Scale-in animation for the text
    this.levelCompleteText.setScale(0);
    this.tweens.add({
      targets: this.levelCompleteText,
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      ease: 'Back.easeOut',
    });

    // Launch confetti/fireworks effect
    this.confettiEffect = new ConfettiEffect(this, GAME_WIDTH, GAME_HEIGHT);
    this.confettiEffect.start();
  }

  private showPressAnyKeyPrompt(): void {
    if (!this.mapData) {
      return;
    }

    const centerX = tileToPixelX(this.mapData.width / 2);
    const centerY = tileToPixelY(this.mapData.height / 2 + 5);

    this.pressAnyKeyText = this.add.text(centerX, centerY, 'PRESS ANY KEY', {
      fontFamily: '"Rajdhani", "Orbitron", "Segoe UI", sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#DDEEFF',
    }).setOrigin(0.5).setDepth(90).setAlpha(0);

    // Fade in the prompt
    this.tweens.add({
      targets: this.pressAnyKeyText,
      alpha: { from: 0, to: 1 },
      duration: PROMPT_FADE_IN_MS,
      ease: 'Sine.easeIn',
    });

    // Add gentle pulsing effect
    this.tweens.add({
      targets: this.pressAnyKeyText,
      alpha: { from: 1, to: 0.4 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: PROMPT_FADE_IN_MS,
    });

    // Listen for any key or click to advance
    this.input.keyboard?.once('keydown', () => {
      this.transitionToNextLevel();
    });
    this.input.once('pointerdown', () => {
      this.transitionToNextLevel();
    });
  }

  private transitionToNextLevel(): void {
    // Clean up level complete text
    if (this.levelCompleteText) {
      this.levelCompleteText.destroy();
      this.levelCompleteText = null;
    }

    // Clean up press any key text
    if (this.pressAnyKeyText) {
      this.pressAnyKeyText.destroy();
      this.pressAnyKeyText = null;
    }

    // Clean up confetti effect
    if (this.confettiEffect) {
      this.confettiEffect.destroy();
      this.confettiEffect = null;
    }

    this.levelCompletePhase = 'none';

    // Transition to LevelTransitionScene with game state
    this.scene.start('LevelTransitionScene', {
      score: this.gameState.score,
      lives: this.gameState.lives,
      level: this.gameState.level + 1,
      extraLifeAwarded: this.gameState.extraLifeAwarded,
      collectedFruits: this.gameState.collectedFruits,
    });
  }

  private checkGhostCollision(): void {
    if (!this.pacman) {
      return;
    }

    const pacPixelX = this.pacman.getPixelX();
    const pacPixelY = this.pacman.getPixelY();

    for (const ghost of this.ghosts) {
      if (!ghost.isReleased()) {
        continue;
      }

      // Skip returning ghosts (eyes only, not collidable)
      if (ghost.isReturning()) {
        continue;
      }

      const dx = ghost.getPixelX() - pacPixelX;
      const dy = ghost.getPixelY() - pacPixelY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < COLLISION_DISTANCE_PX) {
        if (ghost.isFrightened()) {
          // Eat the ghost!
          this.handleGhostEat(ghost);
        } else {
          this.handlePacmanDeath();
          return;
        }
      }
    }
  }

  private handleGhostEat(ghost: GhostEntity): void {
    // Calculate escalating points: 200, 400, 800, 1600
    const comboIndex = Math.min(this.gameState.ghostCombo, SCORING.GHOST_EAT_COMBO.length - 1);
    const points = SCORING.GHOST_EAT_COMBO[comboIndex] ?? 200;
    this.gameState.ghostCombo++;

    // Award points
    this.gameState.score += points;
    this.updateHud();
    this.checkExtraLife();

    // Play ghost eat sound
    playGhostEatSound(this);

    // Show score popup at ghost position
    if (this.scorePopups) {
      this.scorePopups.spawn(ghost.getPixelX(), ghost.getPixelY(), points);
    }

    // Show particle burst at ghost position
    if (this.ghostEatBursts) {
      this.ghostEatBursts.spawn(ghost.getPixelX(), ghost.getPixelY(), ghost.getColor());
    }

    // Ghost enters returning mode (eyes only, heads back to base)
    ghost.setReturning();
  }

  private handlePacmanDeath(): void {
    this.gameState.lives--;
    this.updateHud();

    // Cancel any active vulnerability
    this.isVulnerabilityActive = false;
    this.vulnerabilityTimer = 0;
    for (const ghost of this.ghosts) {
      ghost.setFrightened(false);
    }

    // Play death sound
    playDeathSound(this);

    // Screen shake for impact
    this.cameras.main.shake(250, 0.01);

    // Hide ghosts during death animation
    for (const ghost of this.ghosts) {
      ghost.setVisible(false);
    }

    if (this.gameState.lives <= 0) {
      // Game over - play death animation then show game over overlay
      this.isDying = true;
      this.deathTimer = 0;
      this.deathPhase = 'animation';
      this.isGameOver = true;

      if (this.pacman) {
        this.pacman.startDeathAnimation();
      }

      // After death animation, launch game over overlay
      this.time.delayedCall(DEATH_ANIMATION_MS + 300, () => {
        playGameOverSound(this);
        this.scene.launch('GameOverScene', {
          score: this.gameState.score,
          level: this.gameState.level,
        });
        this.scene.pause();
      });
      return;
    }

    // Start death animation sequence
    this.isDying = true;
    this.deathTimer = 0;
    this.deathPhase = 'animation';

    // Start Pac-Man death animation
    if (this.pacman) {
      this.pacman.startDeathAnimation();
    }
  }

  private respawnAfterDeath(): void {
    if (!this.mapData) {
      return;
    }

    // Reset Pac-Man position
    if (this.pacman) {
      this.pacman.resetPosition();
      this.pacman.setVisible(true);
    }

    // Play warp-in animation for Pac-Man
    this.playWarpInAnimation();

    // Reset ghosts to base
    const releaseX = this.mapData.ghostReleasePoint.x;
    const releaseY = this.mapData.ghostReleasePoint.y;
    for (const ghost of this.ghosts) {
      ghost.resetToBase(releaseX, releaseY);
      ghost.setVisible(true);
    }

    // Reset ghost mode cycle (restart scatter/chase timer)
    this.ghostModeCycleIndex = 0;
    this.ghostModeTimer = 0;
    this.currentGhostBehavior = 'scatter';

    // Show READY! text and pause
    this.isReady = false;
    this.readyTimer = 0;
    this.showReadyText();
  }

  private playWarpInAnimation(): void {
    if (!this.pacman || !this.mapData) {
      return;
    }

    // Start Pac-Man invisible and scaled down
    this.pacman.setScale(0);
    this.pacman.setAlpha(0);

    const graphics = this.pacman.getGraphics();

    // Animate scale from 0 to 1 with bounce easing
    this.tweens.add({
      targets: graphics,
      scaleX: 1,
      scaleY: 1,
      duration: 600,
      ease: 'Back.easeOut',
    });

    // Fade in from 0 to 1
    this.tweens.add({
      targets: graphics,
      alpha: 1,
      duration: 400,
      ease: 'Sine.easeIn',
    });

    // Create a warp glow ring effect at spawn position
    const spawnX = tileToPixelX(this.mapData.pacmanSpawn.x);
    const spawnY = tileToPixelY(this.mapData.pacmanSpawn.y);
    const warpGlow = this.add.graphics();
    warpGlow.lineStyle(2, 0xffff00, 0.8);
    warpGlow.strokeCircle(spawnX, spawnY, 4);

    // Expand the glow ring outward and fade
    this.tweens.add({
      targets: warpGlow,
      scaleX: 4,
      scaleY: 4,
      alpha: 0,
      duration: 700,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        warpGlow.destroy();
      },
    });
  }

  private checkExtraLife(): void {
    if (
      !this.gameState.extraLifeAwarded &&
      this.gameState.score >= SCORING.EXTRA_LIFE_THRESHOLD
    ) {
      this.gameState.extraLifeAwarded = true;
      this.gameState.lives++;
      this.updateHud();
      playExtraLifeSound(this);
    }
  }

  private updateHud(): void {
    if (!this.hud) {
      return;
    }
    this.hud.setScore(this.gameState.score);
    this.hud.setLives(this.gameState.lives);
    this.hud.setLevel(this.gameState.level);
    this.hud.setCollectedFruits(this.gameState.collectedFruits);
  }

  private loadHighScore(): void {
    if (!this.hud) {
      return;
    }
    const topScores = getTopScores(1);
    const highScore = topScores.length > 0 && topScores[0] ? topScores[0].score : 0;
    this.hud.setHighScore(highScore);
  }

  private setupPauseKeys(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      return;
    }

    this.pauseKeyHandler = (event: KeyboardEvent) => {
      // Don't allow pause during death, game over, or level complete
      if (this.isDying || this.isGameOver || this.isLevelComplete) {
        return;
      }

      const key = event.key.toUpperCase();
      const code = event.code;

      const isPauseKey =
        key === 'ESCAPE' ||
        key === 'P' ||
        code === 'F3' ||
        code === 'Pause';

      if (isPauseKey) {
        event.preventDefault();
        this.togglePause();
      }

    };

    keyboard.on('keydown', this.pauseKeyHandler);
  }

  private togglePause(): void {
    if (this.isPaused) {
      // Unpause handled by PauseScene - this shouldn't be called while paused
      // since GameScene is paused, but just in case
      return;
    }

    // Pause the game
    this.isPaused = true;

    // Launch the PauseScene overlay on top
    this.scene.launch('PauseScene');

    // Pause this scene (stops update loop and timers)
    this.scene.pause();
  }

  private cleanup(): void {
    // Kill all active tweens in this scene to prevent lingering references
    this.tweens.killAll();

    // Cancel all pending delayed calls (prevents game-over callback firing after cleanup)
    this.time.removeAllEvents();

    // Remove stored event listeners
    if (this.resumeHandler) {
      this.events.off('resume', this.resumeHandler);
      this.resumeHandler = null;
    }
    if (this.pauseKeyHandler && this.input.keyboard) {
      this.input.keyboard.off('keydown', this.pauseKeyHandler);
      this.pauseKeyHandler = null;
    }

    if (this.pacman) {
      this.pacman.destroy();
      this.pacman = null;
    }
    for (const ghost of this.ghosts) {
      ghost.destroy();
    }
    this.ghosts = [];
    if (this.fruit) {
      this.fruit.destroy();
      this.fruit = null;
    }
    if (this.hud) {
      this.hud.destroy();
      this.hud = null;
    }
    if (this.scorePopups) {
      this.scorePopups.destroy();
      this.scorePopups = null;
    }
    if (this.ghostEatBursts) {
      this.ghostEatBursts.destroy();
      this.ghostEatBursts = null;
    }
    if (this.readyText) {
      this.readyText.destroy();
      this.readyText = null;
    }
    if (this.levelCompleteText) {
      this.levelCompleteText.destroy();
      this.levelCompleteText = null;
    }
    if (this.pressAnyKeyText) {
      this.pressAnyKeyText.destroy();
      this.pressAnyKeyText = null;
    }
    if (this.confettiEffect) {
      this.confettiEffect.destroy();
      this.confettiEffect = null;
    }
    if (this.renderedMap) {
      for (const tween of this.renderedMap.powerPillTweens) {
        tween.destroy();
      }
    }
    this.renderedMap = null;
    this.mapData = null;
    this.mazeGraph = null;
    // Properly destroy keyboard input to remove its listeners
    if (this.keyboardInput) {
      this.keyboardInput.destroy();
      this.keyboardInput = null;
    }
    if (this.mobileInput) {
      this.mobileInput.destroy();
      this.mobileInput = null;
    }
    if (this.fpsText) {
      this.fpsText.destroy();
      this.fpsText = null;
    }
  }
}
